/**
 * The reading pane: one ticket, worked in place.
 *
 * Rendered in two homes — embedded beside the inbox list, and full-width at
 * /tickets/:id for deep links — so the inbox and the "ticket workspace" cannot
 * drift apart; they are the same component.
 *
 * The load-bearing idea survives from the original workspace: the screen shows
 * its working. The customer sidebar is the agent's evidence (orders with their
 * payment state, subscriptions, the guarantee window, the refund block), and
 * the full trace — facts, refusals, tool calls, spend — is one click away
 * behind "Show working" rather than gone.
 *
 * The draft panel carries the three verbs of the console:
 *   Send        — the draft as drafted, behind the server's reply policy.
 *   Edit & send — the human's rewrite; the diff is recorded as a Correction
 *                 by the same request that sends. That capture is the
 *                 improvement loop's entire input, so it costs one click.
 *   Hand off    — a person takes over, with a mandatory reason.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  ChevronRight,
  Download,
  PenLine,
  Send,
  ShieldAlert,
  TriangleAlert,
  UserRound,
  X,
} from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../../api/client';
import { Badge, Button, Card, CardHeader } from '../ui/primitives';
import { cn } from '../../lib/cn';
import { formatDateTime, formatMicroUsd, formatRelative, formatUsd, withBillingLinkStandIn } from '../../lib/format';
import { intentLabel } from '../../lib/inbox/rows';
import { readCustomerPanel, type CustomerPanel } from '../../lib/ticket/customer';
import { readRunFacts, readSpend } from '../../lib/ticket/detail';
import { RunTraceSections } from '../trace/RunTraceSections';
import { ZendeskTicketRef } from './ZendeskTicketRef';
import { diffWords } from '../../lib/ticket/diff';
import { modelRetryOf, runStage, type RunStage } from '../../lib/ticket/run-stage';
import {
  buildTicketMarkdown,
  ticketExportFilename,
  type ExportMessage,
  type ExportRun,
  type ExportTicket,
  type ExportToolCall,
} from '../../lib/ticket/export';
import { downloadMarkdown } from '../../lib/playground/transcript';

interface ApiMessage extends ExportMessage {
  id: string;
}

interface ApiToolCall extends ExportToolCall {
  id: string;
}

interface ApiRun extends ExportRun {
  toolCalls: ApiToolCall[];
}

interface ApiTicketDetail extends ExportTicket {
  messages: ApiMessage[];
  runs: ApiRun[];
}

export function TicketPane({ ticketId }: { ticketId: string }) {
  const [traceOpen, setTraceOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => apiGet<ApiTicketDetail>(`/api/tickets/${ticketId}`),
    enabled: Boolean(ticketId),
    // While the newest run is still being drafted, look again until it is.
    refetchInterval: (query) => {
      const newest = query.state.data?.runs[0];
      const stage = newest ? runStage(newest) : 'done';
      // A model-outage wait is minutes long: look again each minute.
      return stage === 'working' ? 10_000 : stage === 'waiting' ? 60_000 : false;
    },
  });

  const run = data?.runs[0];
  const stage = run ? runStage(run) : 'done';
  // The reason a person took over lives on the run that handed over. A
  // Re-check run since then has none, and must not hide it ("No reason
  // recorded" on #1926700, Deivydas 2026-10-19).
  const handoffRun = data?.runs.find((r) => r.escalated && r.escalationReason) ?? run;
  const facts = useMemo(() => readRunFacts(run?.facts), [run]);
  const draftBlocked = facts.violations.some((v) => v.severity !== 'flag');
  const panel = useMemo(() => readCustomerPanel(run?.facts), [run]);

  if (isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-stop-700" aria-hidden />
        <p className="text-sm text-ink-700">
          {error instanceof Error ? error.message : 'That ticket could not be loaded.'}
        </p>
        <Link to="/inbox" className="mt-3 inline-block text-sm text-accent-700 underline">
          Back to the inbox
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/* conversation + draft */}
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ink-900">
              {data.customerName ?? data.customerEmail ?? 'Unknown sender'}
            </div>
            <div className="truncate font-mono text-2xs text-ink-400">
              <ZendeskTicketRef externalId={data.externalId} /> · {data.brand.name} ·{' '}
              {intentLabel(data.intent)}
            </div>
          </div>
          {run ? (
            // A run solved with no reply (a sales pitch, a thank-you, an
            // autoresponder) ends as "drafted" with no draft — say what
            // actually happened instead (#1921833, 2026-10-12).
            solvedWithoutReply(run.facts) ? (
              <Badge tone="fact">solved · no reply</Badge>
            ) : (
              <Badge tone={run.escalated ? 'stop' : 'neutral'}>
                {run.escalated
                  ? 'escalated'
                  : stage === 'working'
                    ? 'working…'
                    : stage === 'waiting'
                      ? 'waiting for the model'
                      : stage === 'interrupted'
                      ? 'interrupted'
                      : run.status}
              </Badge>
            )
          ) : null}
          {data.openedAt ? (
            // When the ticket came in (Deivydas 2026-10-12), with the age beside it.
            <span className="whitespace-nowrap text-2xs text-ink-500" title={new Date(data.openedAt).toISOString()}>
              Created <span className="tabular text-ink-700">{formatDateTime(data.openedAt)}</span> ·{' '}
              {formatRelative(data.openedAt)}
            </span>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            title="Download the whole ticket — conversation, every run's working, tool calls and raw facts — as a .md file to share"
            onClick={() => downloadMarkdown(ticketExportFilename(data), buildTicketMarkdown(data))}
          >
            <Download size={13} strokeWidth={2} aria-hidden />
            Export .md
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setTraceOpen((open) => !open)}
            aria-expanded={traceOpen}
          >
            Show working
            <ChevronRight
              size={13}
              strokeWidth={2}
              className={cn('transition-transform duration-fast', traceOpen && 'rotate-90')}
              aria-hidden
            />
          </Button>
        </div>

        <div className="space-y-3 px-4 py-4">
          {data.messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'rounded border border-line px-3 py-2 text-sm',
                message.role === 'customer' ? 'bg-surface-sunken' : 'bg-surface',
              )}
            >
              <p className="mb-1 text-2xs uppercase tracking-wide text-ink-400">
                {/* Imported from Zendesk with the ticket (Sophie's, 2026-10-26). */}
                {message.role === 'system' ? 'staff · from Zendesk' : message.role}
                {message.sentAt ? '' : ' · not sent'}
              </p>
              <p className="whitespace-pre-wrap text-ink-700">{message.body}</p>
            </div>
          ))}
          {data.messages.length === 0 ? (
            <p className="text-sm text-ink-400">No messages on this ticket.</p>
          ) : null}

          {run?.escalated || data.status === 'escalated' ? (
            <div className="flex items-start gap-2 rounded border border-line bg-surface px-3 py-2.5">
              <ShieldAlert size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-stop-700" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink-900">Handed to a person</p>
                <p className="text-sm text-ink-500">
                  {handoffRun?.escalationReason ?? 'No reason recorded.'}
                </p>
                {data.status === 'escalated' ? (
                  <p className="mt-1 text-xs text-ink-400">
                    The agent stays silent on this ticket until a person replies from here, or it
                    is released.
                  </p>
                ) : null}
              </div>
              {data.status === 'escalated' ? <ReleaseButton ticketId={data.id} /> : null}
            </div>
          ) : null}

          {facts.violations.length > 0 ? (
            <Card>
              <CardHeader
                // Only a block refuses the draft; flags alone still send (#1920168).
                title={draftBlocked ? 'The draft was refused' : 'Flagged (sent anyway)'}
                meta="Bot rules run on the finished text, outside the prompt"
              />
              <div className="space-y-2 px-4 py-3">
                {facts.violations.map((violation, index) => (
                  <div key={index} className="rounded border border-line px-3 py-2">
                    <p className="text-2xs uppercase tracking-wide text-stop-700">
                      {violation.ruleId.replace(/_/g, ' ')}
                    </p>
                    <p className="mt-1 text-sm italic text-ink-700">&ldquo;{violation.evidence}&rdquo;</p>
                    <p className="mt-1 text-sm text-ink-500">{violation.explanation}</p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          <DraftPanel
            ticketId={data.id}
            run={run ?? null}
            blocked={draftBlocked}
            stopReason={facts.stopReason}
            stage={stage}
          />
        </div>
      </div>

      <CustomerSidebar
        panel={panel}
        priorTickets={data.priorTickets}
        run={run ?? null}
      />

      {traceOpen ? (
        <TracePanel run={run ?? null} facts={facts} onClose={() => setTraceOpen(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer sidebar — the agent's evidence
// ---------------------------------------------------------------------------

function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function CustomerSidebar({
  panel,
  priorTickets,
  run,
}: {
  panel: CustomerPanel;
  priorTickets: number | null;
  run: ApiRun | null;
}) {
  return (
    <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-line bg-surface lg:block">
      {/* The trace, exactly as the playground shows it — routing, condition
          chips, established facts, the order-tags projection, tools — always
          visible, because this sidebar is where ops analyses real tickets
          (Deivydas, 2026-09-11). The customer evidence follows below it. */}
      {run ? (
        <RunTraceSections
          run={{
            workflowSlug: run.workflowSlug,
            variantId: run.variantId,
            confidence: run.confidence,
            tags: run.tags,
            facts: run.facts,
            toolCalls: run.toolCalls,
          }}
        />
      ) : null}

      {!run || panel.empty ? (
        <p className="px-4 py-4 text-xs text-ink-400">
          {run
            ? 'Intake recorded no customer facts on this run.'
            : 'No run yet — intake has not reached this ticket.'}
        </p>
      ) : (
        <>
          <section className="border-b border-line px-4 py-3">
            <h3 className="eyebrow mb-2 flex items-center gap-1.5">
              <UserRound size={12} strokeWidth={1.75} aria-hidden />
              Customer
            </h3>
            {panel.email ? (
              <p className="break-all font-mono text-xs text-ink-700">{panel.email}</p>
            ) : (
              <p className="text-xs text-ink-400">Identity not resolved.</p>
            )}
            {panel.identitySource === 'channel' ? (
              <p className="mt-1 text-2xs text-ink-400">From the ticket — not asked for.</p>
            ) : null}
            {priorTickets !== null ? (
              <p className="mt-1 text-2xs text-ink-400">
                {priorTickets === 0 ? 'First ticket from this customer.' : `${priorTickets} prior ticket${priorTickets === 1 ? '' : 's'}.`}
              </p>
            ) : null}
          </section>

          <section className="border-b border-line px-4 py-3">
            <h3 className="eyebrow mb-2">Orders</h3>
            {panel.orders.length === 0 ? (
              <p className="text-xs text-ink-400">No orders on this email.</p>
            ) : (
              <ul className="space-y-2.5">
                {panel.orders.map((order) => (
                  <li key={order.orderId}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-xs text-ink-900">{order.orderId}</span>
                      {order.wasCharged === false ? (
                        <Badge tone="stop">{order.paymentStatus ?? 'not collected'}</Badge>
                      ) : order.paymentStatus ? (
                        <Badge tone={order.paymentStatus === 'Refunded' ? 'held' : 'fact'}>
                          {order.paymentStatus}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-0.5 flex items-center justify-between text-2xs text-ink-400">
                      <span>
                        {[formatDay(order.createdAt), order.stage].filter(Boolean).join(' · ') || '—'}
                      </span>
                      {order.totalCents !== null ? (
                        <span className="tabular">{formatUsd(order.totalCents)}</span>
                      ) : null}
                    </div>
                    {order.wasCharged === false ? (
                      <p className="mt-0.5 text-2xs text-hold-700">Payment never collected.</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {panel.uncollectedPaymentOverride ? (
              <p className="mt-2 text-2xs text-hold-700">
                The run answered about the uncharged order because the customer raised the failed
                payment.
              </p>
            ) : null}
          </section>

          <section className="border-b border-line px-4 py-3">
            <h3 className="eyebrow mb-2">Subscriptions</h3>
            {panel.subscriptions.length === 0 ? (
              <p className="text-xs text-ink-400">None active.</p>
            ) : (
              <ul className="space-y-2">
                {panel.subscriptions.map((subscription) => (
                  <li key={subscription.subscriptionId} className="text-xs">
                    <div className="text-ink-900">
                      {subscription.itemDescription ?? subscription.subscriptionId}
                    </div>
                    <div className="text-2xs text-ink-400">
                      {[
                        subscription.frequency,
                        subscription.nextShipmentAt
                          ? `next ${formatDay(subscription.nextShipmentAt)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="px-4 py-3">
            <h3 className="eyebrow mb-2">Guarantee</h3>
            {panel.guaranteeStatus ? (
              <p
                className={cn(
                  'text-xs',
                  panel.guaranteeStatus === 'inside' ? 'text-ok-700' : 'text-stop-700',
                )}
              >
                {panel.guaranteeStatus}
                {panel.guaranteeWindowDays !== null ? ` · ${panel.guaranteeWindowDays}-day window` : ''}
              </p>
            ) : (
              <p className="text-xs text-ink-400">Not evaluated.</p>
            )}
            {panel.hasRefundBlock ? (
              <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-stop-700">
                <Ban size={12} strokeWidth={2} aria-hidden />
                Refund block on the account
              </p>
            ) : (
              <p className="mt-1.5 text-2xs text-ink-400">No refund block.</p>
            )}
          </section>
        </>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
/**
 * Release an escalated ticket back to the agent — the mirror of Hand off.
 *
 * Sticky escalation (Deivydas 2026-09-16) means the agent stays silent once a
 * ticket is with a human; a person replying from the console releases it
 * automatically, and this button covers every other case — the human handled
 * it in Zendesk, or ops wants the agent back on it.
 */
function ReleaseButton({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const release = useMutation({
    mutationFn: () => apiPost<{ status: string }>(`/api/tickets/${ticketId}/release`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  return (
    <Button
      size="sm"
      variant="ghost"
      className="shrink-0"
      disabled={release.isPending}
      onClick={() => release.mutate()}
      title="The agent answers this ticket again from the next customer message"
    >
      {release.isPending ? 'Releasing…' : 'Release to Zoye'}
    </Button>
  );
}

// Draft panel — Send / Edit & send / Hand off, plus the shadow review
// ---------------------------------------------------------------------------

interface ReplyState {
  allowed: boolean;
  explanation: string;
  refusal?: string;
  dryRun: boolean;
  adapter: 'live' | 'mock';
  adapterReason: string | null;
}

const EDIT_CATEGORIES = [
  { key: 'tone', label: 'tone' },
  { key: 'missing_fact', label: 'missing fact' },
  { key: 'wrong_policy', label: 'wrong policy' },
  { key: 'other', label: 'other' },
] as const;

function DraftPanel({
  ticketId,
  run,
  blocked,
  stopReason,
  stage,
}: {
  ticketId: string;
  run: ApiRun | null;
  blocked: boolean;
  stopReason: string | null;
  stage: RunStage;
}) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<'idle' | 'send' | 'edit' | 'handoff'>('idle');
  const [outcome, setOutcome] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ['reply-state', ticketId],
    queryFn: () => apiGet<ReplyState>(`/api/tickets/${ticketId}/reply-state`),
    enabled: Boolean(run?.draftReply),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    await queryClient.invalidateQueries({ queryKey: ['reply-state', ticketId] });
    await queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };

  const send = useMutation({
    mutationFn: (input: { body?: string; category?: string }) =>
      apiPost<{ explanation: string; correctionId?: string | null }>(
        `/api/tickets/${ticketId}/reply`,
        input,
      ),
    onSuccess: async (result) => {
      setOutcome(
        result.correctionId
          ? `${result.explanation} Your edit was recorded as a correction.`
          : result.explanation,
      );
      setAction('idle');
      await refresh();
    },
    onError: (error: unknown) => {
      setOutcome(error instanceof ApiError ? error.message : 'That did not go through.');
    },
  });

  const handoff = useMutation({
    mutationFn: (reason: string) =>
      apiPost<{ status: string }>(`/api/tickets/${ticketId}/handoff`, { reason }),
    onSuccess: async () => {
      setOutcome('Handed off — the ticket is marked for a person.');
      setAction('idle');
      await refresh();
    },
    onError: (error: unknown) => {
      setOutcome(error instanceof ApiError ? error.message : 'That did not go through.');
    },
  });

  return (
    <Card>
      <CardHeader
        title="Draft reply"
        meta={
          run?.draftReply
            ? blocked
              ? 'Kept for review — it will not be sent'
              : run.mode === 'assisted'
                ? 'Waiting for a person to send it'
                : `This brand is in ${run.mode}`
            : stage === 'working'
              ? 'Zoye is drafting'
              : stage === 'waiting'
                ? 'Waiting for the AI model'
                : 'Nothing drafted'
        }
      />

      {action === 'edit' && run?.draftReply ? (
        <EditAndSend
          draft={run.draftReply}
          pending={send.isPending}
          dryRun={state.data?.dryRun ?? true}
          onCancel={() => setAction('idle')}
          onSend={(body, category) => send.mutate({ body, ...(category ? { category } : {}) })}
        />
      ) : (
        <div className="px-4 py-4">
          {run?.draftReply ? (
            <p className="whitespace-pre-wrap text-sm text-ink-700">
              {withBillingLinkStandIn(run.draftReply)}
            </p>
          ) : (
            <p className="text-sm text-ink-400">
              {stage === 'working'
                ? 'Zoye has read the account and is writing the reply. The draft appears here on its own, usually within a minute or two.'
                : stage === 'waiting'
                  ? waitingNote(run?.facts)
                  : stage === 'interrupted'
                  ? 'This run stopped before a draft was written — usually a deploy or restart cut it off, and the job ran again as a newer run. It can also be a repeat of a message that was already answered.'
                  : 'This run produced no draft.'}
              {stage === 'done' && stopReason ? ` Stopped: ${stopReason.replace(/_/g, ' ')}.` : ''}
            </p>
          )}
        </div>
      )}

      {run?.draftReply && action !== 'edit' ? (
        <div className="border-t border-line px-4 py-3">
          {state.data?.adapter === 'mock' ? (
            <p className="mb-2 flex items-start gap-1.5 text-xs text-hold-700">
              <ShieldAlert size={13} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
              {state.data.adapterReason ?? 'Zendesk is not configured.'} Nothing sent from here
              reaches a customer.
            </p>
          ) : null}
          {state.data ? <p className="mb-2 text-xs text-ink-500">{state.data.explanation}</p> : null}

          {action === 'send' ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                onClick={() => send.mutate({})}
                disabled={send.isPending}
              >
                {send.isPending ? 'Sending…' : 'Yes, send it'}
              </Button>
              <Button variant="ghost" onClick={() => setAction('idle')} disabled={send.isPending}>
                Cancel
              </Button>
            </div>
          ) : action === 'handoff' ? (
            <HandoffForm
              pending={handoff.isPending}
              onCancel={() => setAction('idle')}
              onConfirm={(reason) => handoff.mutate(reason)}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {state.data?.allowed ? (
                <>
                  {/* Two steps, because a send cannot be undone. */}
                  <Button variant="primary" onClick={() => setAction('send')}>
                    <Send size={14} strokeWidth={2} aria-hidden />
                    {state.data.dryRun ? 'Send (dry run)' : 'Send'}
                  </Button>
                  <Button onClick={() => setAction('edit')}>
                    <PenLine size={14} strokeWidth={2} aria-hidden />
                    Edit &amp; send
                  </Button>
                </>
              ) : null}
              <Button variant="danger" onClick={() => setAction('handoff')}>
                Hand off
              </Button>
            </div>
          )}

          {outcome ? <p className="mt-2 text-xs text-ink-700">{outcome}</p> : null}
        </div>
      ) : null}

      {!run?.draftReply && run ? (
        <div className="border-t border-line px-4 py-3">
          {action === 'handoff' ? (
            <HandoffForm
              pending={handoff.isPending}
              onCancel={() => setAction('idle')}
              onConfirm={(reason) => handoff.mutate(reason)}
            />
          ) : (
            <Button variant="danger" onClick={() => setAction('handoff')}>
              Hand off
            </Button>
          )}
          {outcome ? <p className="mt-2 text-xs text-ink-700">{outcome}</p> : null}
        </div>
      ) : null}

      {run ? <ReviewBlock ticketId={ticketId} /> : null}
    </Card>
  );
}

/**
 * The edit surface. The live diff is display-only — the raw pair is what the
 * server records — but seeing it while typing is what makes the correction a
 * deliberate lesson rather than an accident.
 */
function EditAndSend({
  draft,
  pending,
  dryRun,
  onCancel,
  onSend,
}: {
  draft: string;
  pending: boolean;
  dryRun: boolean;
  onCancel: () => void;
  onSend: (body: string, category: string | null) => void;
}) {
  const [text, setText] = useState(draft);
  const [category, setCategory] = useState<string | null>(null);
  const tokens = useMemo(() => diffWords(draft, text), [draft, text]);
  const changed = tokens.some((token) => token.kind !== 'same');

  return (
    <div className="px-4 py-4">
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={8}
        aria-label="Edited reply"
        className="w-full rounded border border-line bg-surface px-3 py-2 text-sm text-ink-900"
      />

      <div className="mt-2 rounded border border-line bg-surface-sunken px-3 py-2 text-sm leading-relaxed">
        {changed ? (
          tokens.map((token, index) => (
            <span
              key={index}
              className={cn(
                token.kind === 'del' && 'bg-stop-100 text-stop-700 line-through',
                token.kind === 'add' && 'bg-ok-100 text-ok-700',
              )}
            >
              {token.text}
            </span>
          ))
        ) : (
          <span className="text-ink-400">No changes yet — this would send the draft as is.</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-500">What was wrong?</span>
        {EDIT_CATEGORIES.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={category === option.key}
            onClick={() => setCategory((current) => (current === option.key ? null : option.key))}
            className={cn(
              'rounded-sm px-2 py-0.5 text-2xs font-semibold transition-colors duration-fast',
              category === option.key
                ? 'bg-accent-50 text-accent-700 shadow-hairline'
                : 'bg-surface-sunken text-ink-500 shadow-hairline hover:text-ink-900',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="mt-2 text-2xs text-ink-400">
        Sending records the edit as a correction — that is how Zoye learns.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={pending || !changed}
          onClick={() => onSend(text, category)}
        >
          {pending ? 'Sending…' : dryRun ? 'Send edited reply (dry run)' : 'Send edited reply'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function HandoffForm({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <div>
      <textarea
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={2}
        placeholder="Why does this need a person? The next reader starts here."
        aria-label="Handoff reason"
        className="mb-2 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="danger"
          disabled={pending || reason.trim().length === 0}
          onClick={() => onConfirm(reason.trim())}
        >
          {pending ? 'Handing off…' : 'Hand off to a person'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The shadow-mode verdict: would a person have sent this? A disagreement
 * becomes an eval case — the suite grows out of ops doing its job.
 */
function ReviewBlock({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (agreed: boolean) =>
      apiPost<{ caseSlug: string | null }>(`/api/tickets/${ticketId}/review`, {
        agreed,
        note: note.trim() || undefined,
      }),
    onSuccess: async (result, agreed) => {
      setSaved(
        agreed
          ? 'Recorded — you would have sent it.'
          : result.caseSlug
            ? `Recorded, and saved as eval case ${result.caseSlug}.`
            : 'Recorded.',
      );
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });

  return (
    <div className="border-t border-line px-4 py-3">
      <p className="mb-2 text-2xs uppercase tracking-wide text-ink-400">Would you have sent this?</p>
      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        rows={2}
        placeholder="If not — what should have happened instead? That sentence becomes the test."
        className="mb-2 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => review.mutate(true)} disabled={review.isPending}>
          <Check size={14} strokeWidth={2} aria-hidden />
          I&rsquo;d have sent it
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => review.mutate(false)}
          disabled={review.isPending}
        >
          <X size={14} strokeWidth={2} aria-hidden />
          I would not
        </Button>
        {saved ? <span className="text-sm text-ok-700">{saved}</span> : null}
        {review.error ? (
          <span className="text-sm text-stop-700">
            {review.error instanceof Error ? review.error.message : 'Could not record that.'}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trace panel — "show working", docked over the right edge
// ---------------------------------------------------------------------------

function TracePanel({
  run,
  facts,
  onClose,
}: {
  run: ApiRun | null;
  facts: ReturnType<typeof readRunFacts>;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-label="Run trace"
      className="fixed inset-y-0 right-0 z-40 w-[420px] max-w-full overflow-y-auto border-l border-line bg-paper shadow-overlay"
    >
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        <h2 className="text-sm font-semibold text-ink-900">The run&rsquo;s working</h2>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}>
          <X size={14} strokeWidth={2} aria-hidden />
          Close
        </Button>
      </div>

      <div className="space-y-4 p-4">
        {/* The trace sections themselves live in the always-visible sidebar;
            this panel carries what does not fit there — the indeterminacies,
            the gated writes, and the spend. */}
        {facts.indeterminate.length > 0 ? (
          <Card>
            <CardHeader title="Could not be determined" meta="Why a person was needed" />
            <ul className="space-y-1 px-4 py-3 text-sm text-ink-700">
              {facts.indeterminate.map((reason) => (
                <li key={reason}>· {reason}</li>
              ))}
            </ul>
          </Card>
        ) : null}

        {facts.proposedWrites.length > 0 ? (
          <Card>
            <CardHeader
              title="Writes Zoye called"
              meta="Auto mode executes the allowed ones at once — each one's outcome (Done, Pre-approved, Blocked) is in Approvals"
            />
            <div className="space-y-2 px-4 py-3">
              {facts.proposedWrites.map((write, index) => (
                <div key={index} className="rounded border border-line px-3 py-2">
                  <p className="flex items-center gap-1.5 font-mono text-xs text-ink-700">
                    <Ban size={12} strokeWidth={2} className="text-hold-700" aria-hidden />
                    {write.tool}
                  </p>
                  <p className="mt-1 text-2xs text-ink-400">
                    {Object.entries(write.arguments)
                      .map(([key, value]) => `${key}: ${String(value)}`)
                      .join(' · ')}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        {run ? <SpendCard facts={run.facts} /> : null}
      </div>
    </div>
  );
}

/**
 * What this one ticket cost, by role — the interesting question is never the
 * total, it is whether the expensive model is doing the cheap model's job.
 */
function SpendCard({ facts }: { facts: unknown }) {
  const spend = readSpend(facts);
  if (!spend) return null;

  return (
    <Card>
      <CardHeader
        title="Cost"
        meta={`${(spend.inputTokens + spend.outputTokens).toLocaleString()} tokens · ${spend.latencyMs}ms`}
      />
      <div className="px-4 py-3">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-sm text-ink-500">This ticket</span>
          <span className="tabular text-lg font-semibold text-ink-900">
            {formatMicroUsd(spend.costMicroUsd)}
          </span>
        </div>

        <ul className="space-y-1">
          {spend.perRole.map((role) => (
            <li key={role.role} className="flex items-center justify-between text-xs">
              <span className="text-ink-700">{role.role.replace(/_/g, ' ')}</span>
              <span className="tabular text-ink-400">
                {role.inputTokens.toLocaleString()} in / {role.outputTokens.toLocaleString()} out ·{' '}
                {formatMicroUsd(role.costMicroUsd)}
              </span>
            </li>
          ))}
        </ul>

        {spend.unpriced.length > 0 ? (
          <p className="mt-2 text-2xs text-hold-700">
            No price configured for {spend.unpriced.join(', ')}, so this run reports no cost. Set it
            in Admin → Models — the tokens are recorded and can be costed later.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/** The run closed the ticket on purpose, with no reply: a no-reply topic or an autoresponder. */
function solvedWithoutReply(facts: unknown): boolean {
  if (typeof facts !== 'object' || facts === null) return false;
  const record = facts as { disposition?: { kind?: unknown }; automatedMessage?: unknown };
  return record.disposition?.kind === 'close' || record.automatedMessage === true;
}

/** What a run waiting out a model outage is doing, and when it tries again. */
function waitingNote(facts: unknown): string {
  const wait = modelRetryOf(facts);
  const at = wait ? new Date(wait.nextAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    'The AI model could not be reached, so Zoye is waiting to try again' +
    (wait && at ? ` at ${at} (try ${wait.attempt} of ${wait.of})` : '') +
    '. If the model is still down after the last try, the ticket goes to a person.'
  );
}
